/**
 * What a learner has and has not reached in a program.
 *
 * Pure TypeScript: no React, no network, no storage. It takes the program as
 * the API serves it plus whatever progress documents are at hand, and answers
 * three questions the interface needs: is this milestone done, which one is
 * current, and is this program open yet.
 *
 * ## Why this is derived and not stored
 *
 * A milestone's identity in the database is recreated on every content resync,
 * so a stored milestone state would be wiped each time a file is corrected.
 * The pieces and exercises, on the other hand, are keyed by identifiers written
 * in the files themselves and survive. Deriving from those costs one pass over
 * a handful of arrays and can never go stale against the content.
 */

import type {
  MilestoneGate,
  PieceStage,
  ProgramDetail,
  ProgramListItem,
  ProgramMilestone,
} from '@/lib/api/api-client';
import type { PhraseState } from './types';

/** Journey order. `DONE` sits above every stage. */
const STAGE_RANK: Record<PhraseState, number> = {
  S0: 0,
  S1a: 1,
  S1b: 2,
  S2a: 3,
  S2b: 4,
  S3: 5,
  S4: 6,
  S5: 7,
  DONE: 8,
};

/** The slice of a journey document this module needs, and nothing more. */
export type PieceProgressLike = {
  readonly phrases: readonly { readonly etatMax: PhraseState }[];
};

/** The slice of an exercise progress document this module needs. */
export type ExerciseProgressLike = {
  readonly reachedStage?: PhraseState;
  readonly completedAt?: number;
};

export type MilestoneState = 'locked' | 'current' | 'done';

export type MilestoneStatus = {
  readonly milestone: ProgramMilestone;
  readonly state: MilestoneState;
  /** Share of the piece's units that already reached the gate, in `[0, 1]`. */
  readonly reached: number;
};

export type ProgramState = {
  readonly milestones: readonly MilestoneStatus[];
  readonly doneCount: number;
  readonly isComplete: boolean;
};

export type ProgressSources = {
  /** Journey documents by `midiFileId`. */
  readonly pieces: ReadonlyMap<string, PieceProgressLike>;
  /** Exercise documents by VEF id. */
  readonly exercises: ReadonlyMap<string, ExerciseProgressLike>;
};

function rank(stage: PhraseState | PieceStage): number {
  return STAGE_RANK[stage as PhraseState] ?? 0;
}

/**
 * Share of a piece's units that reached the gate's stage.
 *
 * A piece is not one object that flips: it is a list of work units, each with
 * its own furthest state. Reporting the share rather than a boolean is what
 * lets the interface say "9 units out of 12" instead of a bare "not yet",
 * which is the difference between progress and a wall.
 */
function pieceReach(progress: PieceProgressLike | undefined, stage: PieceStage): number {
  if (!progress || progress.phrases.length === 0) {
    return 0;
  }

  const target = rank(stage);
  const reached = progress.phrases.filter((p) => rank(p.etatMax) >= target).length;

  return reached / progress.phrases.length;
}

function gateReach(
  milestone: ProgramMilestone,
  gate: MilestoneGate | null,
  sources: ProgressSources,
): number {
  // No gate declared: the milestone is crossed as soon as it is opened. That is
  // deliberate in the format, so an author can add a step without inventing a
  // criterion for it.
  if (!gate) {
    return 1;
  }

  if (gate.type === 'piece_stage') {
    if (milestone.kind !== 'piece' || !milestone.midiFileId) {
      return 0;
    }

    return pieceReach(sources.pieces.get(milestone.midiFileId), gate.stage);
  }

  if (gate.type === 'exercises_reached_stage') {
    const ids =
      milestone.kind === 'step'
        ? milestone.exercises.map((e) => e.id)
        : milestone.remedies.map((r) => r.vefId);

    if (ids.length === 0) {
      return 0;
    }

    const target = rank(gate.stage);
    const done = ids.filter((id) => {
      const stage = sources.exercises.get(id)?.reachedStage;

      return stage !== undefined && rank(stage) >= target;
    }).length;

    // `count` is a number of exercises, never a share of correctness: a
    // end-of-session score does not measure that something was learned.
    const needed = Math.min(gate.count ?? 1, ids.length);

    return Math.min(1, done / needed);
  }

  // `tempo_reached` needs the run rate, which lives in the journey document
  // this module deliberately does not parse. Treated as not reached rather
  // than silently passed.
  return 0;
}

export function deriveProgramState(
  program: ProgramDetail,
  sources: ProgressSources,
): ProgramState {
  let currentSeen = false;

  const milestones = program.milestones.map((milestone): MilestoneStatus => {
    const reached = gateReach(milestone, milestone.gate, sources);
    const done = reached >= 1;

    if (done) {
      return { milestone, state: 'done', reached };
    }

    // The first unfinished milestone is where the learner is. Everything after
    // it is locked, so the path reads as a path and not as a menu.
    if (!currentSeen) {
      currentSeen = true;

      return { milestone, state: 'current', reached };
    }

    return { milestone, state: 'locked', reached };
  });

  const doneCount = milestones.filter((m) => m.state === 'done').length;

  return {
    milestones,
    doneCount,
    isComplete: milestones.length > 0 && doneCount === milestones.length,
  };
}

/**
 * Programs whose prerequisites are all finished.
 *
 * A program with no prerequisite is always open, which is what makes an entry
 * point an entry point.
 */
export function unlockedProgramIds(
  programs: readonly ProgramListItem[],
  completedVpfIds: ReadonlySet<string>,
): Set<string> {
  return new Set(
    programs
      .filter((p) => p.prerequisites.every((id) => completedVpfIds.has(id)))
      .map((p) => p.vpfId),
  );
}
